import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Globe, Plus, Loader2, X, Trash2, Upload, Eye, Users, Clock, Mail, Copy,
  CheckCircle,
} from 'lucide-react'
import { apiGet, apiPost, apiDelete } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

const PORTAL_TYPE_LABELS: Record<string, { label: string; icon: typeof Upload; colour: string }> = {
  upload: { label: 'Upload', icon: Upload, colour: 'bg-blue-100 text-blue-700' },
  view_only: { label: 'View Only', icon: Eye, colour: 'bg-green-100 text-green-700' },
  collaborate: { label: 'Collaborate', icon: Users, colour: 'bg-purple-100 text-purple-700' },
}

export function PortalPage() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: invitations, isLoading } = useQuery({
    queryKey: ['docflow-portal-invitations'],
    queryFn: () => apiGet<any[]>('/docflow/portal/invitations'),
  })

  // ── Create invitation ─────────────────────────────────────────────────
  const [newInvite, setNewInvite] = useState({
    name: '', email: '', organisation: '',
    portal_type: 'upload' as 'upload' | 'view_only' | 'collaborate',
    scope_type: 'site' as 'site' | 'case',
    scope_id: '', expires_at: '', max_uploads: '', password: '',
  })

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/portal/invitations', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-portal-invitations'] })
      setShowCreateModal(false)
      setNewInvite({ name: '', email: '', organisation: '', portal_type: 'upload', scope_type: 'site', scope_id: '', expires_at: '', max_uploads: '', password: '' })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/docflow/portal/invitations/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['docflow-portal-invitations'] }),
  })

  const inviteList = Array.isArray(invitations) ? invitations : []
  const activeCount = inviteList.filter((i: any) => i.status === 'active' || !i.status).length

  function copyUrl(url: string, id: string) {
    navigator.clipboard.writeText(url)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">External Portal</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Invite contractors, auditors, and inspectors to upload or view documents
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> New Invitation
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Total Invitations</p>
          <p className="text-2xl font-bold text-navy-800">{inviteList.length}</p>
        </div>
        <div className="card py-4 border-green-200">
          <p className="text-xs text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="card py-4 border-blue-200">
          <p className="text-xs text-gray-400">Upload Portals</p>
          <p className="text-2xl font-bold text-blue-600">
            {inviteList.filter((i: any) => i.portal_type === 'upload').length}
          </p>
        </div>
        <div className="card py-4 border-purple-200">
          <p className="text-xs text-gray-400">View / Collaborate</p>
          <p className="text-2xl font-bold text-purple-600">
            {inviteList.filter((i: any) => i.portal_type !== 'upload').length}
          </p>
        </div>
      </div>

      {/* Invitations */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
        </div>
      ) : inviteList.length === 0 ? (
        <div className="card text-center py-16">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No portal invitations</p>
          <p className="text-xs text-gray-400 mt-1">
            Invite contractors to submit certificates or auditors to review documents
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {inviteList.map((invite: any) => {
            const typeInfo = PORTAL_TYPE_LABELS[invite.portal_type] ?? PORTAL_TYPE_LABELS.upload
            const Icon = typeInfo.icon
            const isExpired = invite.expires_at && new Date(invite.expires_at) < new Date()
            return (
              <div
                key={invite.id}
                className={cn('card', isExpired && 'opacity-60')}
              >
                <div className="flex items-start gap-4">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', typeInfo.colour.replace('text-', 'bg-').split(' ')[0])}>
                    <Icon className={cn('w-5 h-5', typeInfo.colour.split(' ')[1])} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-navy-800">{invite.name}</h3>
                      <span className={cn('badge text-xs', typeInfo.colour)}>{typeInfo.label}</span>
                      {invite.scope_type && (
                        <span className="badge text-xs bg-gray-100 text-gray-600">
                          {invite.scope_type}
                        </span>
                      )}
                      {isExpired && <span className="badge text-xs bg-red-100 text-red-700">Expired</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {invite.email}
                      </span>
                      {invite.organisation && <span>{invite.organisation}</span>}
                      {invite.expires_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Expires {formatDate(invite.expires_at)}
                        </span>
                      )}
                      {invite.max_uploads != null && (
                        <span>Max {invite.max_uploads} uploads</span>
                      )}
                    </div>
                    {invite.portal_url && (
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-xs bg-gray-50 rounded px-2 py-1 text-gray-600 truncate max-w-md">
                          {invite.portal_url}
                        </code>
                        <button
                          onClick={() => copyUrl(invite.portal_url, invite.id)}
                          className="text-gray-400 hover:text-navy-800 transition-colors"
                          title="Copy URL"
                        >
                          {copiedId === invite.id ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(`Revoke invitation for ${invite.name}?`)) revokeMutation.mutate(invite.id)
                    }}
                    disabled={revokeMutation.isPending}
                    className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Revoke"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Invitation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                New Portal Invitation
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!newInvite.name || !newInvite.email || !newInvite.scope_id) return
                const payload: Record<string, unknown> = {
                  name: newInvite.name,
                  email: newInvite.email,
                  portal_type: newInvite.portal_type,
                  scope_type: newInvite.scope_type,
                  scope_id: newInvite.scope_id,
                }
                if (newInvite.organisation) payload.organisation = newInvite.organisation
                if (newInvite.expires_at) payload.expires_at = new Date(newInvite.expires_at).toISOString()
                if (newInvite.max_uploads) payload.max_uploads = parseInt(newInvite.max_uploads)
                if (newInvite.password) payload.password = newInvite.password
                createMutation.mutate(payload)
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    value={newInvite.name}
                    onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                    className="input" placeholder="Dave Thompson" required
                  />
                </div>
                <div>
                  <label className="label">Email *</label>
                  <input
                    type="email"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                    className="input" placeholder="dave@example.co.uk" required
                  />
                </div>
              </div>

              <div>
                <label className="label">Organisation</label>
                <input
                  value={newInvite.organisation}
                  onChange={(e) => setNewInvite({ ...newInvite, organisation: e.target.value })}
                  className="input" placeholder="Smith Electrical Ltd"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Portal Type *</label>
                  <select
                    value={newInvite.portal_type}
                    onChange={(e) => setNewInvite({ ...newInvite, portal_type: e.target.value as any })}
                    className="input"
                  >
                    <option value="upload">Upload — submit documents</option>
                    <option value="view_only">View Only — read access</option>
                    <option value="collaborate">Collaborate — view + comment</option>
                  </select>
                </div>
                <div>
                  <label className="label">Scope Type *</label>
                  <select
                    value={newInvite.scope_type}
                    onChange={(e) => setNewInvite({ ...newInvite, scope_type: e.target.value as any })}
                    className="input"
                  >
                    <option value="site">Property / Site</option>
                    <option value="case">Case</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Scope ID * <span className="text-gray-400 font-normal">(DocFlow Site or Case ID)</span></label>
                <input
                  value={newInvite.scope_id}
                  onChange={(e) => setNewInvite({ ...newInvite, scope_id: e.target.value })}
                  className="input" placeholder="UUID or case reference" required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Expires</label>
                  <input
                    type="date"
                    value={newInvite.expires_at}
                    onChange={(e) => setNewInvite({ ...newInvite, expires_at: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Max Uploads</label>
                  <input
                    type="number" min={1}
                    value={newInvite.max_uploads}
                    onChange={(e) => setNewInvite({ ...newInvite, max_uploads: e.target.value })}
                    className="input" placeholder="5"
                  />
                </div>
                <div>
                  <label className="label">Password</label>
                  <input
                    type="text"
                    value={newInvite.password}
                    onChange={(e) => setNewInvite({ ...newInvite, password: e.target.value })}
                    className="input" placeholder="Optional"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
                <button
                  type="submit"
                  disabled={!newInvite.name || !newInvite.email || !newInvite.scope_id || createMutation.isPending}
                  className="btn-primary"
                >
                  {createMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4" /> Send Invitation</>
                  )}
                </button>
              </div>
              {createMutation.isError && <p className="text-sm text-red-600">Failed to create invitation.</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
